import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, StickyNote } from "lucide-react";

export function NotesPanel({ clientId }) {
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = () => api.get(`/admin/clients/${clientId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try { await api.post(`/admin/clients/${clientId}/notes`, { text: draft }); setDraft(""); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try { await api.patch(`/admin/clients/${clientId}/notes/${id}`, { text: editText }); setEditingId(null); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/clients/${clientId}/notes/${id}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="client-notes-panel" className="space-y-6 max-w-2xl">
      <div className="rounded-lg border border-[#E4E9F0] bg-[#F8FAFC]/60 p-4 shadow-sm space-y-3">
        <div className="text-[11px] font-mono uppercase tracking-[0.14em] font-bold text-[#0C2347] flex items-center gap-2"><StickyNote size={13} /> New internal note</div>
        <Textarea data-testid="client-note-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add an internal note about this client…" rows={3} className="rounded-lg bg-white" />
        <Button data-testid="client-note-add" disabled={saving || !draft.trim()} onClick={add} className="rounded-lg bg-[#0C2347] hover:bg-[#123262] text-white">{saving ? "Saving…" : "Add note"}</Button>
      </div>
      <div className="space-y-3">
        {notes === null && <div className="text-sm text-slate-400">Loading notes…</div>}
        {notes?.length === 0 && <div data-testid="client-notes-empty" className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-lg">No notes yet.</div>}
        {notes?.map((n) => (
          <div key={n.id} data-testid={`client-note-${n.id}`} className="group rounded-lg border border-[#E4E9F0] border-l-4 border-l-[#0C2347] bg-white px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
            {editingId === n.id ? (
              <div className="space-y-2">
                <Textarea data-testid={`client-note-edit-input-${n.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="rounded-lg" />
                <div className="flex gap-2"><Button data-testid={`client-note-edit-save-${n.id}`} size="sm" className="rounded-lg bg-[#0C2347] hover:bg-[#123262] text-white" onClick={() => saveEdit(n.id)}>Save</Button><Button size="sm" variant="ghost" className="rounded-lg" onClick={() => setEditingId(null)}>Cancel</Button></div>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#101828] leading-relaxed whitespace-pre-wrap">{n.text}</p>
                <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[#F1F5F9]">
                  <span className="text-[11px] font-mono uppercase tracking-[0.08em] text-[#94A3B8] bg-[#F7F9FC] px-2 py-0.5 rounded">{n.updated_by ? `Edited by ${n.updated_by} · ${new Date(n.updated_at).toLocaleString()}` : `Added by ${n.created_by} · ${new Date(n.created_at).toLocaleString()}`}</span>
                  <div className="flex gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`client-note-edit-${n.id}`} onClick={() => { setEditingId(n.id); setEditText(n.text); }} className="text-[#475467] hover:text-[#0C2347] hover:bg-[#F1F5F9] p-1.5 rounded-md"><Pencil size={13} /></button>
                    <button data-testid={`client-note-delete-${n.id}`} onClick={() => remove(n.id)} className="text-[#475467] hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md"><Trash2 size={13} /></button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
